import { useState, useMemo } from "react";
import { Drawer } from "@mui/material";
import vegIcon from "../../../../../../assets/images/veg-new.png";
import nonVegIcon from "../../../../../../assets/images/non-veg-new.png";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import "./ComboModal.css";

const getVariantImage = (variant) =>
  variant?.images?.front ||
  variant?.images?.back ||
  variant?.images?.left ||
  variant?.images?.right ||
  variant?.images?.top ||
  variant?.images?.bottom ||
  null;

const FALLBACK =
  "https://tse4.mm.bing.net/th?id=OIP.mPwk4FItjNeb2Oaq8F1u9QHaGE&pid=Api&P=0";

const ComboModal = ({
  open,
  onClose,
  product,
  sellingPrice,
  index,
  onAddItem,
  saleMode,
}) => {
  const [pricingMode, setPricingMode] = useState("regular");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState("");
  const [kitchenNotes, setKitchenNotes] = useState("");
  const [qty, setQty] = useState(1);
  // true once the user touches the qty stepper — untouched Add = plain add (+1),
  // touched Add = set the absolute quantity
  const [qtyEdited, setQtyEdited] = useState(false);

  const showSnackbar = useSnackbar();

  const basePrice = Number(sellingPrice) || 0;

  const finalPrice = useMemo(() => {
    if (pricingMode === "complimentary") return 0;
    if (pricingMode === "discount") {
      const val = parseFloat(discountValue) || 0;
      if (discountType === "percent") return Math.max(0, basePrice - (basePrice * val) / 100);
      return Math.max(0, basePrice - val);
    }
    return basePrice;
  }, [pricingMode, discountType, discountValue, basePrice]);

  const handleAdd = () => {
    onAddItem(
      product?.variants?.[0]?.barcodes?.[0],
      product?.name,
      index,
      "Standard",
      undefined,
      product?.variants?.[0]?.variantId,
      pricingMode === "complimentary" ? 0 : finalPrice,
      undefined,
      undefined,
      true,
      product?.productId,
      kitchenNotes || undefined,
      qtyEdited ? qty : null
    );
    onClose();
  };

  const productImage = getVariantImage(product?.variants?.[0]) || product?.image || FALLBACK;
  const bundleVariants = product?.variants || [];
  const shortCode = product?.variants?.[0]?.shortCode;
  const orderCount = product?.orderCount || product?.totalOrders || null;
  const foodType = product?.foodType || product?.variants?.[0]?.foodType;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: "80vw",
          borderRadius: 0,
          display: "flex",
          flexDirection: "row",
          overflow: "hidden",
        },
      }}
    >
      <div className="cmo-container">

        {/* ── Left panel ── */}
        <div className="cmo-left">
          {/* Image with overlays */}
          <div className="cmo-img-wrap">
            <img src={productImage} alt={product?.name} className="cmo-img" />

            {/* Top badges */}
            <div className="cmo-top-badges">
              <span className="cmo-badge-combo">Combo Meal</span>
              <span className="cmo-badge-includes">Includes {bundleVariants?.length} items</span>
            </div>

            {/* Close button inside image */}
            <button className="cmo-close-btn" onClick={onClose} aria-label="Close">✕</button>

            {/* Bottom meta overlay */}
            <div className="cmo-img-meta">
              <div className="cmo-img-meta-name-row">
                {foodType && (
                  <img
                    src={foodType === "VEG" ? vegIcon : nonVegIcon}
                    alt={foodType}
                    className="cmo-food-dot"
                  />
                )}
                <span className="cmo-img-product-name">{product?.name || "—"}</span>
                {shortCode && <span className="cmo-img-shortcode">{shortCode}</span>}
              </div>
              <div className="cmo-img-meta-price-row">
                {orderCount && (
                  <span className="cmo-ordered-times">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                    Ordered {orderCount} times before
                  </span>
                )}
                <span className="cmo-img-price">₹{basePrice}</span>
              </div>
            </div>
          </div>

          {/* Pricing card */}
          <div className="cmo-pricing-card">
            <div className="cmo-pricing-header">
              <span className="cmo-pricing-title">Pricing</span>
              <span className="cmo-pricing-hint">Choose one</span>
            </div>

            <label className={`cmo-pricing-option${pricingMode === "regular" ? " cmo-pricing-option--active" : ""}`}>
              <span className="cmo-option-label">Regular item</span>
              <input type="radio" name="pricingMode" checked={pricingMode === "regular"}
                onChange={() => { setPricingMode("regular"); setDiscountValue(""); }} className="cmo-radio" />
            </label>

            <label className="cmo-pricing-option cmo-pricing-option--disabled"
              onClick={(e) => { e.preventDefault(); showSnackbar("Feature is coming soon", "warning"); }}>
              <span className="cmo-option-label">Item discount</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ color: "#9ca3af", flexShrink: 0 }}>
                <path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/>
                <path d="m15 9-6 6"/>
                <path d="M9 9h.01"/>
                <path d="M15 15h.01"/>
              </svg>
            </label>

            {pricingMode === "discount" && (
              <div className="cmo-discount-row">
                <button className={`cmo-discount-type-btn${discountType === "percent" ? " active" : ""}`}
                  onClick={() => setDiscountType("percent")}>%</button>
                <button className={`cmo-discount-type-btn${discountType === "rupee" ? " active" : ""}`}
                  onClick={() => setDiscountType("rupee")}>₹</button>
                <input type="number" min="0"
                  placeholder={discountType === "percent" ? "e.g. 10" : "e.g. 50"}
                  value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                  className="cmo-discount-input" />
              </div>
            )}

            <label className="cmo-pricing-option cmo-pricing-option--disabled"
              onClick={(e) => { e.preventDefault(); showSnackbar("Feature is coming soon", "warning"); }}>
              <span className="cmo-option-label">Complimentary</span>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ color: "#9ca3af", flexShrink: 0 }}>
                <rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13"/>
                <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"/>
                <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"/>
              </svg>
            </label>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div className="cmo-right">
          {/* Close button — top-right of drawer */}
          <button className="cmo-drawer-close-btn" onClick={onClose} aria-label="Close">✕</button>

          <div className="cmo-right-body">

            {/* Combo includes card */}
            <div className="cmo-section-card">
              <div className="cmo-section-header">
                <div>
                  <h3 className="cmo-right-title">Combo includes</h3>
                  <p className="cmo-right-subtitle">Everything included in this combo meal.</p>
                </div>
                <span className="cmo-items-badge">{bundleVariants?.length} items</span>
              </div>
              <div className="cmo-items-row">
                {bundleVariants?.map((variant, i) => (
                  <div key={i} className="cmo-item-card">
                    <div className="cmo-item-img-wrap">
                      <img
                        src={getVariantImage(variant) || FALLBACK}
                        alt={variant?.name}
                        className="cmo-item-img"
                        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK; }}
                      />
                    </div>
                    <p className="cmo-item-name">
                      <span className="cmo-item-num">{i + 1}</span>
                      {variant?.name || `Item ${i + 1}`}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes to Kitchen card */}
            <div className="cmo-section-card">
              <div className="cmo-notes-header">
                <span className="cmo-notes-title">Notes to Kitchen</span>
                <span className="cmo-notes-optional">Optional</span>
              </div>
              <textarea
                className="cmo-notes-input"
                placeholder="Add cooking or plating instructions"
                value={kitchenNotes}
                onChange={(e) => setKitchenNotes(e.target.value)}
                rows={3}
              />
            </div>

          </div>

          {/* Footer */}
          <div className="cmo-footer">
            <div className="cmo-total">
              <span className="cmo-total-label">Total Amount</span>
              <span className="cmo-total-price">
                {pricingMode === "complimentary" ? (
                  <><span className="cmo-total-strike">₹{basePrice * qty}</span> <span className="cmo-total-free">₹0</span></>
                ) : `₹${(finalPrice * qty).toFixed(2).replace(/\.00$/, "")}`}
              </span>
            </div>
            <div className="cmo-footer-actions">
              <div className="cmo-qty-control">
                <button className="cmo-qty-btn" onClick={() => { setQtyEdited(true); setQty((q) => Math.max(1, q - 1)); }} disabled={qty <= 1}>−</button>
                <span className="cmo-qty-value">{qty}</span>
                <button className="cmo-qty-btn" onClick={() => { setQtyEdited(true); setQty((q) => q + 1); }}>+</button>
              </div>
              <button className="cmo-add-btn" onClick={handleAdd}>
                Add Item
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </div>
        </div>

      </div>
    </Drawer>
  );
};

export default ComboModal;
